import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { APP_UPDATE_CHECK_MS, buildKey, decideUpdate, isSafeUpdateRoute, isUiBusy, type AppBuildInfo, type DocLike } from '@nexops/shared';
import { tStatic } from '@/i18n/I18nProvider';
import { apiRequestsInFlight } from '@/shared/lib/api';
import { APP_BUILD } from '@/shared/lib/app-version';
import { queryClient } from '@/shared/lib/queryClient';
import { SAFE_ROUTES } from './updateScreens';
import { fetchRemoteBuild } from './versionCheck';

/** `visibilitychange` i `focus` przychodzą parą przy każdym powrocie do karty; jedno pytanie wystarczy. Sam powrót pyta zawsze. */
const MIN_GAP_MS = 10_000;
/** Pierwsze pytanie czeka chwilę, żeby nie konkurować z żądaniami startowymi strony. */
const FIRST_CHECK_DELAY_MS = 3_000;
/** Odpowiedź, która przychodzi tak krótko po powrocie do karty, nadal liczy się jako „właśnie wróciłem”. */
const RESUME_WINDOW_MS = 20_000;
/** Przed automatycznym przeładowaniem: poczekaj, aż żądania panelu się uspokoją — ale nie w nieskończoność. */
const QUIET_MAX_MS = 6_000;
/** Przycisk czeka krócej: użytkownik sam o to poprosił. */
const QUIET_MAX_MANUAL_MS = 4_000;
/** sessionStorage: build serwera, dla którego przeładowanie było już próbowane w tej karcie (straż przed pętlą). */
const GUARD_KEY = 'nexops:update.reloadAttemptedFor';
const TOAST_ID = 'app-update';

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function readStore(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** false, gdy nie dało się zapisać — bez straży przeładowanie, które nie pomaga, mogłoby się powtarzać w nieskończoność,
 *  więc wołający nie może wtedy przeładować automatycznie. */
function writeStore(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return window.sessionStorage.getItem(key) === value;
  } catch {
    return false;
  }
}

/** Dwa spokojne spojrzenia z rzędu, co ćwierć sekundy — albo false po limicie. Spokój = żadne żądanie API w locie
 *  i żaden zapis (mutacja) w toku. */
async function waitForQuiet(maxMs: number): Promise<boolean> {
  const end = Date.now() + maxMs;
  let calm = 0;
  while (Date.now() < end) {
    calm = apiRequestsInFlight() === 0 && queryClient.isMutating() === 0 ? calm + 1 : 0;
    if (calm >= 2) return true;
    await sleep(250);
  }
  return false;
}

/**
 * Otwarta karta panelu sama dowiaduje się, że na serwerze stoi inny build (`/version.json`), i wymienia się na niego,
 * gdy to bezpieczne — port `AppUpdateWatcher` z ZUR OPS (reguły: `decideUpdate` w `@nexops/shared`).
 *
 * Pyta po powrocie do karty, po odzyskaniu fokusu i sieci oraz co 15 min. Wynik:
 *   • reload — powrót do karty po ≥ 30 s, ekran z listy bezpiecznych (`updateScreens.ts`), brak okna i wpisanego
 *     tekstu, nic się nie zapisuje. Nawet wtedy najpierw czeka, aż żądania panelu się uspokoją, i rozstrzyga jeszcze
 *     raz tuż przed przeładowaniem.
 *   • banner — jest inny build, ale to nie ten moment: komunikat z przyciskiem „Odśwież teraz” (zamknięty nie wraca
 *     dla tego samego builda).
 * Nigdy nie przeładowuje karty schowanej ani w trakcie pracy. Nieudana albo bezużyteczna odpowiedź niczego nie zmienia.
 */
export function useAppUpdate() {
  const { pathname } = useLocation();
  /* Nasłuchy poniżej rejestrują się raz; bieżącą trasę czytają stąd. */
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  /** Build serwera, dla którego użytkownik zamknął komunikat — nie wraca do niego, dopóki nie wyjdzie inny. */
  const dismissedFor = useRef<string | null>(null);
  const reloading = useRef(false);

  const applyNow = useCallback(async () => {
    if (reloading.current) return;
    reloading.current = true;
    /* Zapis w toku nie może zostać urwany. Po limicie przeładowujemy mimo to — użytkownik kliknął, a zapis i tak jest
       po stronie serwera albo wróci błędem. */
    await waitForQuiet(QUIET_MAX_MANUAL_MS);
    window.location.reload();
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    let cancelled = false;
    let checking = false;
    /** Każde sprawdzenie bierze numer; nowsze wymuszone unieważnia starsze, więc pytanie, które wisi przez pobyt w tle,
     *  nie połknie tego zadanego przy powrocie. */
    let seq = 0;
    let lastStart = 0;
    let hiddenAt: number | null = null;
    let resume: { hiddenForMs: number; at: number } | null = null;

    /** null, chyba że to sprawdzenie jest (jeszcze) powrotem do karty. */
    const hiddenForMs = (): number | null => (resume && Date.now() - resume.at <= RESUME_WINDOW_MS ? resume.hiddenForMs : null);

    function showToast(server: AppBuildInfo) {
      const key = buildKey(server);
      if (dismissedFor.current === key) return;
      toast(tStatic('appu.available', { version: server.version }), {
        id: TOAST_ID,
        duration: Infinity,
        description: tStatic('appu.availableHint'),
        action: { label: tStatic('appu.refreshNow'), onClick: () => void applyNow() },
        onDismiss: () => {
          dismissedFor.current = key;
        },
      });
    }

    async function check(force: boolean) {
      if (cancelled || document.visibilityState === 'hidden') return;
      if (!force && (checking || Date.now() - lastStart < MIN_GAP_MS)) return;
      const mine = ++seq;
      const stale = () => cancelled || mine !== seq;
      checking = true;
      lastStart = Date.now();
      try {
        const server = await fetchRemoteBuild();
        if (stale() || !server) return; // brak odpowiedzi → nic nie zmieniaj

        const input = () => ({
          server,
          client: APP_BUILD,
          hiddenForMs: hiddenForMs(),
          safeScreen: isSafeUpdateRoute(pathnameRef.current, SAFE_ROUTES),
          hasUnsyncedWork: queryClient.isMutating() > 0,
          uiBusy: isUiBusy(document as unknown as DocLike),
          online: navigator.onLine,
          reloadAttemptedFor: readStore(GUARD_KEY),
        });

        let decision = decideUpdate(input());
        if (decision === 'none') {
          toast.dismiss(TOAST_ID);
          return;
        }
        if (decision === 'reload') {
          /* Moment wyglądał na bezpieczny; niech żądania panelu się uspokoją i spójrz jeszcze raz — użytkownik mógł
             w międzyczasie coś otworzyć. */
          const quiet = await waitForQuiet(QUIET_MAX_MS);
          if (stale()) return;
          decision = decideUpdate(input());
          if (decision === 'reload' && quiet && document.visibilityState === 'visible' && writeStore(GUARD_KEY, buildKey(server))) {
            reloading.current = true;
            window.location.reload();
            return;
          }
        }
        showToast(server);
      } finally {
        if (mine === seq) checking = false;
      }
    }

    function onVisibility() {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null) {
        resume = { hiddenForMs: Date.now() - hiddenAt, at: Date.now() };
        hiddenAt = null;
      }
      void check(true);
    }
    const onFocus = () => void check(false);
    const onOnline = () => void check(false);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    const interval = window.setInterval(() => void check(false), APP_UPDATE_CHECK_MS);
    /* Jeszcze bez `hiddenAt`, więc to pytanie może najwyżej pokazać komunikat — świeżo załadowana strona jest już
       najnowszą rzeczą, jaką serwer wysłał. */
    const first = window.setTimeout(() => void check(true), FIRST_CHECK_DELAY_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
      window.clearInterval(interval);
      window.clearTimeout(first);
    };
  }, [applyNow]);
}
